/**
 * ScoreRewritePanel — the operator-facing surface of the closed
 * score→rewrite→diff→variations loop (src/lib/content/scoreRewrite).
 *
 * Three trust mechanics, all thin over the pure module:
 *   1. LIVE score meter — five dimensions, each bar carrying its
 *      plain-English "why" as a tooltip, recomputed on every text change.
 *   2. "Fix this" per failing dimension — routes through the REAL worker
 *      (deterministic fallback when absent) and shows the old→new line
 *      diff BEFORE anything is applied. Approve applies via the pure
 *      `applyRewrite`; Undo restores the exact prior draft. Never clobbers.
 *   3. Variations — n distinct-angle, pre-scored, lineage-linked siblings
 *      rendered side-by-side with A/B tags; "Pick best" applies the
 *      highest-scoring one.
 *
 * The panel owns NO content strings and fabricates NO data: every piece of
 * text comes from the draft, the worker, or the deterministic fallback,
 * and every score comes from the deterministic breakdown.
 */

import { useMemo, useState } from 'react';
import {
  Check,
  Gauge,
  GitCompare,
  Loader2,
  Shuffle,
  Sparkles,
  Trophy,
  Undo2,
  Wrench,
  X,
} from 'lucide-react';
import { GlassPanel } from './GlassPanel';
import { C } from '../lib/tokens';
import * as argoWorker from '../lib/agent/argoWorker';
import { FORMAT_SPECS, type GenContext } from '../lib/content/generate';
import {
  DIMENSIONS,
  DIMENSION_LABELS,
  applyRewrite,
  bestVariation,
  generateVariations,
  rewriteForFix,
  scoreBreakdown,
  weakestDimensions,
  type Dimension,
  type RewriteResult,
  type Variation,
} from '../lib/content/scoreRewrite';

const AB_TAGS = ['A', 'B', 'C', 'D', 'E', 'F'];

function scoreColor(score: number): string {
  return score >= 70 ? C.green : score >= 40 ? C.orange : C.orangeWarm;
}

interface FixState {
  status: 'idle' | 'loading' | 'ready' | 'error';
  dim?: Dimension;
  result?: RewriteResult;
  error?: string;
}

interface VarsState {
  status: 'idle' | 'loading' | 'done' | 'error';
  items: Variation[];
  error?: string;
}

export interface ScoreRewritePanelProps {
  /** The current draft text (controlled by the parent). */
  text: string;
  /** The original brief — variations are siblings of this, not of the draft. */
  brief: string;
  /** Format id (key of FORMAT_SPECS). */
  format: string;
  ctx: GenContext;
  /** True when the real worker is configured (VITE_AGENT_API_URL). */
  workerReady: boolean;
  /** Parent applies the new text (approve / undo / pick-variation). */
  onApply: (next: string) => void;
}

export function ScoreRewritePanel({
  text,
  brief,
  format,
  ctx,
  workerReady,
  onApply,
}: ScoreRewritePanelProps) {
  const spec = FORMAT_SPECS[format];
  const [fix, setFix] = useState<FixState>({ status: 'idle' });
  const [vars, setVars] = useState<VarsState>({ status: 'idle', items: [] });
  const [undoStack, setUndoStack] = useState<string[]>([]);
  const [pickedBest, setPickedBest] = useState(false);

  // Live: recomputed on every keystroke the parent passes down.
  const breakdown = useMemo(
    () => (spec ? scoreBreakdown(text, spec, ctx.voice) : null),
    [text, spec, ctx.voice],
  );
  const failing = useMemo(
    () => (breakdown ? weakestDimensions(breakdown) : []),
    [breakdown],
  );

  if (!spec || !breakdown) return null;
  const totalColor = scoreColor(breakdown.total);
  const worker = workerReady ? argoWorker : undefined;

  const handleFix = async (dim: Dimension) => {
    setFix({ status: 'loading', dim });
    try {
      const result = await rewriteForFix(text, spec, ctx, [dim], { worker });
      setFix({ status: 'ready', dim, result });
    } catch (err) {
      setFix({
        status: 'error',
        dim,
        error: err instanceof Error ? err.message : 'Rewrite failed.',
      });
    }
  };

  const handleApprove = () => {
    if (fix.status !== 'ready' || !fix.result) return;
    try {
      const next = applyRewrite(text, fix.result.diff); // throws on stale diff
      setUndoStack((s) => [...s, text]);
      setFix({ status: 'idle' });
      onApply(next);
    } catch (err) {
      setFix({
        status: 'error',
        dim: fix.dim,
        error: err instanceof Error ? err.message : 'Diff no longer applies.',
      });
    }
  };

  const handleUndo = () => {
    setUndoStack((s) => {
      const prev = s[s.length - 1];
      if (prev !== undefined) onApply(prev);
      return s.slice(0, -1);
    });
  };

  const handleVariations = async () => {
    setVars({ status: 'loading', items: [] });
    setPickedBest(false);
    try {
      const items = await generateVariations(brief.trim() || text, format, ctx, 3, { worker });
      setVars({ status: 'done', items });
    } catch (err) {
      setVars({
        status: 'error',
        items: [],
        error: err instanceof Error ? err.message : 'Variation run failed.',
      });
    }
  };

  const applyVariation = (v: Variation) => {
    setUndoStack((s) => [...s, text]);
    onApply(v.text);
  };

  const best = bestVariation(vars.items);

  return (
    <GlassPanel style={{ padding: 16, display: 'grid', gap: 14 }}>
      {/* ---- Live score meter ---- */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Gauge size={16} style={{ color: totalColor }} />
        <span className="t-section-title" style={{ color: C.text }}>
          Score &amp; rewrite loop
        </span>
        <span
          className="glass-pill"
          style={{
            marginLeft: 'auto',
            fontSize: 11,
            padding: '3px 10px',
            background: `${totalColor}22`,
            color: totalColor,
            boxShadow: `inset 0 0 0 1px ${totalColor}55`,
            fontWeight: 600,
          }}
        >
          {breakdown.total}/100
        </span>
        {undoStack.length > 0 && (
          <button
            onClick={handleUndo}
            className="glass-btn"
            style={{ padding: '4px 10px', display: 'flex', alignItems: 'center', gap: 4 }}
            title="Restore the draft as it was before the last applied rewrite"
          >
            <Undo2 size={12} style={{ color: C.textMuted }} />
            <span className="t-meta" style={{ color: C.textMuted }}>
              Undo
            </span>
          </button>
        )}
      </div>

      <div style={{ display: 'grid', gap: 8 }}>
        {DIMENSIONS.map((d) => {
          const ds = breakdown.dims[d];
          const color = scoreColor(ds.score);
          const isFailing = failing.includes(d);
          const fixingThis = fix.status === 'loading' && fix.dim === d;
          return (
            <div key={d} style={{ display: 'grid', gap: 3 }} title={ds.why}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="t-card-title" style={{ fontSize: 12, color: C.text }}>
                  {DIMENSION_LABELS[d]}
                </span>
                <span className="t-meta" style={{ color: C.textMuted }}>
                  {ds.score}/100
                </span>
                {isFailing && (
                  <button
                    onClick={() => void handleFix(d)}
                    disabled={fix.status === 'loading'}
                    className="glass-btn"
                    style={{
                      marginLeft: 'auto',
                      padding: '2px 8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      opacity: fix.status === 'loading' && !fixingThis ? 0.5 : 1,
                    }}
                    title={`Targeted rewrite: ${ds.why}`}
                  >
                    {fixingThis ? (
                      <Loader2 size={11} className="animate-spin" style={{ color: C.orange }} />
                    ) : (
                      <Wrench size={11} style={{ color: C.orange }} />
                    )}
                    <span className="t-meta" style={{ color: C.orange }}>
                      Fix this
                    </span>
                  </button>
                )}
              </div>
              <div
                style={{
                  height: 4,
                  borderRadius: 2,
                  background: 'rgba(255,255,255,0.08)',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${ds.score}%`,
                    height: '100%',
                    borderRadius: 2,
                    background: color,
                  }}
                />
              </div>
              <div className="t-meta" style={{ color: C.textDim }}>
                {ds.why}
              </div>
            </div>
          );
        })}
      </div>

      {/* ---- Reviewable rewrite diff (approve / dismiss) ---- */}
      {fix.status === 'error' && (
        <div className="t-meta" style={{ color: C.orangeWarm }}>
          {fix.error || 'Rewrite failed.'}
        </div>
      )}
      {fix.status === 'ready' && fix.result && fix.dim && (
        <div
          style={{
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 10,
            padding: 12,
            display: 'grid',
            gap: 8,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <GitCompare size={14} style={{ color: C.blueElectric }} />
            <span className="t-card-title" style={{ fontSize: 12, color: C.text }}>
              Proposed fix — {DIMENSION_LABELS[fix.dim]}
            </span>
            <span className="t-meta" style={{ marginLeft: 'auto', color: C.textMuted }}>
              {fix.result.diff.summary} ·{' '}
              {fix.result.sealed.usedWorker ? 'Claude via worker' : 'deterministic fallback'}
            </span>
          </div>
          <div
            style={{
              maxHeight: 220,
              overflowY: 'auto',
              fontFamily: 'monospace',
              fontSize: 12,
              lineHeight: 1.5,
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 8,
              padding: 10,
            }}
          >
            {fix.result.diff.lines.map((l, i) => (
              <div
                key={i}
                style={{
                  whiteSpace: 'pre-wrap',
                  color:
                    l.kind === 'add' ? C.green : l.kind === 'del' ? C.orangeWarm : C.textMuted,
                  textDecoration: l.kind === 'del' ? 'line-through' : 'none',
                }}
              >
                {l.kind === 'add' ? '+ ' : l.kind === 'del' ? '- ' : '  '}
                {l.line || '\u00a0'}
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button
              onClick={() => setFix({ status: 'idle' })}
              className="glass-btn"
              style={{ padding: '5px 12px', display: 'flex', alignItems: 'center', gap: 4 }}
            >
              <X size={12} style={{ color: C.textMuted }} />
              <span className="t-meta" style={{ color: C.textMuted }}>
                Dismiss
              </span>
            </button>
            <button
              onClick={handleApprove}
              className="glass-btn glass-btn--primary"
              style={{ padding: '5px 12px', display: 'flex', alignItems: 'center', gap: 4 }}
            >
              <Check size={12} />
              <span className="t-meta">Approve rewrite</span>
            </button>
          </div>
        </div>
      )}

      {/* ---- Variations: scored, lineage-linked A/B siblings ---- */}
      <div
        style={{
          borderTop: '1px solid rgba(255,255,255,0.08)',
          paddingTop: 12,
          display: 'grid',
          gap: 10,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Shuffle size={14} style={{ color: C.magenta }} />
          <span className="t-card-title" style={{ fontSize: 12, color: C.text }}>
            Variations
          </span>
          <button
            onClick={() => void handleVariations()}
            disabled={vars.status === 'loading'}
            className="glass-btn"
            style={{
              marginLeft: 'auto',
              padding: '4px 10px',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            {vars.status === 'loading' ? (
              <Loader2 size={12} className="animate-spin" style={{ color: C.magenta }} />
            ) : (
              <Sparkles size={12} style={{ color: C.magenta }} />
            )}
            <span className="t-meta" style={{ color: C.magenta }}>
              Generate 3 variations
            </span>
          </button>
          {vars.status === 'done' && best && (
            <button
              onClick={() => {
                applyVariation(best);
                setPickedBest(true);
              }}
              className="glass-btn glass-btn--primary"
              style={{ padding: '4px 10px', display: 'flex', alignItems: 'center', gap: 4 }}
              title={`Highest heuristic total: ${best.score.total}/100 (angle: ${best.angle})`}
            >
              <Trophy size={12} />
              <span className="t-meta">Pick best</span>
            </button>
          )}
        </div>

        {vars.status === 'idle' && (
          <div className="t-meta" style={{ color: C.textDim }}>
            Generate scored siblings of this brief at different angles — each lineage-linked for
            honest A/B provenance.
          </div>
        )}
        {vars.status === 'error' && (
          <div className="t-meta" style={{ color: C.orangeWarm }}>
            {vars.error || 'Variation run failed.'}
          </div>
        )}
        {vars.status === 'done' && vars.items.length === 0 && (
          <div className="t-meta" style={{ color: C.textDim }}>
            No variations produced.
          </div>
        )}
        {vars.items.length > 0 && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${Math.min(vars.items.length, 3)}, 1fr)`,
              gap: 10,
            }}
          >
            {vars.items.map((v, i) => {
              const isBest = best !== null && v === best;
              const color = scoreColor(v.score.total);
              return (
                <div
                  key={`${v.lineage.digest}-${i}`}
                  style={{
                    border: isBest && pickedBest
                      ? `1px solid ${C.green}`
                      : '1px solid rgba(255,255,255,0.12)',
                    borderRadius: 10,
                    padding: 10,
                    display: 'grid',
                    gap: 8,
                    alignContent: 'start',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span
                      className="glass-pill"
                      style={{ fontSize: 10, padding: '2px 8px', fontWeight: 700 }}
                      title={`parent ${v.parentId} · ${v.lineage.usedWorker ? 'worker' : 'fallback'}`}
                    >
                      {AB_TAGS[i] ?? String(i + 1)}
                    </span>
                    <span className="t-meta" style={{ color: C.textDim }}>
                      {v.angle}
                    </span>
                    {isBest && <Trophy size={11} style={{ color: C.green }} />}
                    <span
                      className="glass-pill"
                      style={{
                        marginLeft: 'auto',
                        fontSize: 10,
                        padding: '2px 8px',
                        background: `${color}22`,
                        color,
                        boxShadow: `inset 0 0 0 1px ${color}55`,
                        fontWeight: 600,
                      }}
                      title={DIMENSIONS.map(
                        (d) => `${DIMENSION_LABELS[d]}: ${v.score.dims[d].score} — ${v.score.dims[d].why}`,
                      ).join('\n')}
                    >
                      {v.score.total}
                    </span>
                  </div>
                  <pre
                    style={{
                      margin: 0,
                      whiteSpace: 'pre-wrap',
                      fontFamily: 'monospace',
                      fontSize: 11,
                      lineHeight: 1.5,
                      color: C.text,
                      maxHeight: 160,
                      overflowY: 'auto',
                    }}
                  >
                    {v.text}
                  </pre>
                  <button
                    onClick={() => applyVariation(v)}
                    className="glass-btn"
                    style={{ padding: '4px 10px', justifySelf: 'end' }}
                  >
                    <span className="t-meta" style={{ color: C.textMuted }}>
                      Use this
                    </span>
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </GlassPanel>
  );
}
